/* The Jump to palette's index and matcher, kept pure so the ranking is
 * testable without a DOM. Every destination carries the words people used
 * before the console was folded down (sandboxes, playground, deployments...),
 * so an old habit still lands on the page where that thing lives now. */

export type PaletteGroup = "recent" | "pages" | "agents" | "agentTabs" | "settings";

export type PaletteEntry = {
	/** Stable across sessions: recents are stored by id. */
	id: string;
	group: Exclude<PaletteGroup, "recent">;
	label: string;
	to: string;
	keywords: string[];
	/** Set on agent tabs: the agent the tab belongs to. */
	agent?: { name: string; slug: string };
};

export type PaletteSection = { group: PaletteGroup; label: string; entries: PaletteEntry[] };

export type PaletteAgent = { name: string; slug: string; supportsChat: boolean };

const GROUP_LABELS: Record<PaletteGroup, string> = {
	recent: "Recent",
	pages: "Pages",
	agents: "Agents",
	agentTabs: "Agent tabs",
	settings: "Settings",
};

const GROUP_ORDER: PaletteGroup[] = ["recent", "pages", "agents", "agentTabs", "settings"];

/** Per-group caps: a workspace can hold a whole fleet of agents. */
const GROUP_LIMITS: Partial<Record<PaletteGroup, number>> = { agents: 8, agentTabs: 8 };

export const RECENT_LIMIT = 5;

type StaticEntry = { label: string; to: string; keywords: string[] };

const PAGES: StaticEntry[] = [
	{ label: "Agents", to: "/agents", keywords: ["agent list", "profiles", "home"] },
	{ label: "New agent", to: "/agents/new", keywords: ["create agent", "add agent", "new profile"] },
	{
		label: "Conversations",
		to: "/conversations",
		keywords: ["sessions", "transcripts", "threads", "chats", "history"],
	},
	{ label: "Approvals", to: "/approvals", keywords: ["hitl", "review", "pending", "requests"] },
	{ label: "Usage", to: "/usage", keywords: ["cost", "spend", "tokens", "billing", "metrics"] },
	{
		label: "Chat UI studio",
		to: "/chat-ui",
		keywords: ["embed", "widget", "chat theme", "studio"],
	},
];

const SETTINGS: StaticEntry[] = [
	{
		label: "General",
		to: "/settings/workspace",
		keywords: ["workspace", "theme", "branding", "workspace name", "settings"],
	},
	{
		label: "Model providers",
		to: "/settings/providers",
		keywords: ["models", "llm", "custom providers", "catalog"],
	},
	{
		label: "API keys",
		to: "/settings/api-keys",
		keywords: ["keys", "tokens", "access tokens", "credentials"],
	},
	{
		label: "Members",
		to: "/settings/members",
		keywords: ["team", "users", "invite", "people", "roles"],
	},
	{
		label: "Runtime",
		to: "/settings/runtime",
		keywords: ["sandboxes", "sandbox classes", "limits", "cpu", "memory"],
	},
];

type AgentTab = { tab: string; label: string; keywords: string[]; chatOnly?: boolean };

const AGENT_TABS: AgentTab[] = [
	{ tab: "chat", label: "Chat", keywords: ["playground", "talk", "try"], chatOnly: true },
	{
		tab: "setup",
		label: "Setup",
		keywords: ["general", "files", "prompt", "instructions", "tools", "model", "config"],
	},
	{
		tab: "connections",
		label: "Connections",
		keywords: ["environment", "env", "variables", "secrets", "mcp", "egress", "external apis"],
	},
	{
		tab: "ship",
		label: "Ship",
		keywords: [
			"deployments",
			"deploy",
			"versions",
			"triggers",
			"schedule",
			"webhook",
			"api endpoint",
			"embed",
		],
	},
	{
		tab: "activity",
		label: "Activity",
		keywords: ["runs", "proving", "replay", "conversations", "logs"],
		chatOnly: true,
	},
];

export function buildPaletteEntries(agents: PaletteAgent[]): PaletteEntry[] {
	const statics = (group: "pages" | "settings", entries: StaticEntry[]): PaletteEntry[] =>
		entries.map((entry) => ({ id: `${group}:${entry.to}`, group, ...entry }));

	const agentEntries: PaletteEntry[] = [];
	const tabEntries: PaletteEntry[] = [];
	for (const agent of agents) {
		agentEntries.push({
			id: `agent:${agent.slug}`,
			group: "agents",
			label: agent.name,
			to: `/agents/${agent.slug}`,
			keywords: agent.slug === agent.name ? [] : [agent.slug],
		});
		for (const tab of AGENT_TABS) {
			// A worker serves HTTP instead of running turns: nothing to chat
			// with, and no runs to replay.
			if (tab.chatOnly && !agent.supportsChat) continue;
			tabEntries.push({
				id: `agent:${agent.slug}:${tab.tab}`,
				group: "agentTabs",
				label: tab.label,
				to: `/agents/${agent.slug}/${tab.tab}`,
				keywords: tab.keywords,
				agent: { name: agent.name, slug: agent.slug },
			});
		}
	}

	return [
		...statics("pages", PAGES),
		...agentEntries,
		...tabEntries,
		...statics("settings", SETTINGS),
	];
}

export function tokenize(query: string): string[] {
	return query.toLowerCase().split(/\s+/).filter(Boolean);
}

function isSubsequence(token: string, term: string): boolean {
	let at = 0;
	for (const char of term) {
		if (char === token[at]) at += 1;
		if (at === token.length) return true;
	}
	return false;
}

/** How well one query token matches one term, 0 when it does not. Fuzzy
 * (subsequence) matching is reserved for primary names: on synonyms it
 * turns every short token into noise. */
function scoreTerm(term: string, token: string, fuzzy: boolean): number {
	const value = term.toLowerCase();
	if (value === token) return 100;
	if (value.startsWith(token)) return 85;
	const words = value.split(/[^a-z0-9]+/).filter(Boolean);
	if (words.includes(token)) return 75;
	if (words.some((word) => word.startsWith(token))) return 65;
	if (value.includes(token)) return 40;
	if (fuzzy && token.length >= 3 && isSubsequence(token, value)) return 15;
	return 0;
}

const KEYWORD_WEIGHT = 0.7;
/** A tab matched without naming its agent ("playground") is offered for
 * every agent, so it ranks below pages and settings that match outright. */
const UNANCHORED_TAB_WEIGHT = 0.5;

function bestScore(terms: string[], token: string, fuzzy: boolean, weight = 1): number {
	let best = 0;
	for (const term of terms) best = Math.max(best, scoreTerm(term, token, fuzzy) * weight);
	return best;
}

/**
 * Score an entry against a tokenized query: every token has to match
 * something (label, synonyms or, on a tab, its agent's name), and the
 * entry's score is the sum. Null means "not a match". Agent tabs never show
 * for an empty query: they are the deep end, reached by typing.
 */
export function scoreEntry(entry: PaletteEntry, tokens: string[]): number | null {
	if (tokens.length === 0) return entry.group === "agentTabs" ? null : 0;

	let total = 0;
	let anchored = !entry.agent;
	for (const token of tokens) {
		const own = Math.max(
			bestScore([entry.label], token, true),
			bestScore(entry.keywords, token, entry.group === "agents", KEYWORD_WEIGHT),
		);
		const agent = entry.agent ? bestScore([entry.agent.name, entry.agent.slug], token, true) : 0;
		if (own === 0 && agent === 0) return null;
		if (agent > own) anchored = true;
		total += Math.max(own, agent);
	}
	return anchored ? total : total * UNANCHORED_TAB_WEIGHT;
}

/** Tie-breakers that never outrank a better match: the agent on screen
 * first, then recent picks (and tabs of recently opened agents). */
function affinityBonus(entry: PaletteEntry, recentIds: string[], currentAgent?: string): number {
	const agentId = entry.group === "agents" ? entry.id : entry.agent && `agent:${entry.agent.slug}`;
	if (currentAgent && agentId === `agent:${currentAgent}`) return 0.1;
	const index = recentIds.findIndex(
		(id) => id === entry.id || (!!agentId && (id === agentId || id.startsWith(`${agentId}:`))),
	);
	return index === -1 ? 0 : (RECENT_LIMIT - index) * 0.01;
}

/**
 * Group the entries for display. Empty query: recent picks first, then the
 * pages, agents and settings in their usual order. With a query: matches
 * only, best first within a group, and groups ordered by their best match so
 * Enter on the first option takes the strongest hit. `currentAgent` is the
 * slug of the agent page on screen, if any.
 */
export function searchPalette(
	entries: PaletteEntry[],
	query: string,
	recentIds: string[],
	currentAgent?: string,
): PaletteSection[] {
	const tokens = tokenize(query);
	const recentSet = new Set(tokens.length === 0 ? recentIds : []);

	const scored = new Map<PaletteGroup, { entry: PaletteEntry; score: number }[]>();
	for (const entry of entries) {
		if (recentSet.has(entry.id)) continue;
		const score = scoreEntry(entry, tokens);
		if (score === null) continue;
		const bucket = scored.get(entry.group) ?? [];
		bucket.push({ entry, score: score + affinityBonus(entry, recentIds, currentAgent) });
		scored.set(entry.group, bucket);
	}

	const sections: (PaletteSection & { top: number })[] = [];
	if (recentSet.size > 0) {
		const byId = new Map(entries.map((entry) => [entry.id, entry]));
		const recent = recentIds.flatMap((id) => byId.get(id) ?? []);
		if (recent.length > 0) {
			sections.push({ group: "recent", label: GROUP_LABELS.recent, entries: recent, top: 0 });
		}
	}
	for (const group of GROUP_ORDER) {
		const bucket = scored.get(group);
		if (!bucket?.length) continue;
		// Array.prototype.sort is stable, so equal scores keep index order.
		if (tokens.length > 0) bucket.sort((a, b) => b.score - a.score);
		sections.push({
			group,
			label: GROUP_LABELS[group],
			entries: bucket.slice(0, GROUP_LIMITS[group]).map(({ entry }) => entry),
			top: bucket[0].score,
		});
	}

	if (tokens.length > 0) sections.sort((a, b) => b.top - a.top);
	return sections.map(({ top: _top, ...section }) => section);
}

/** Most recent first, deduplicated, capped. */
export function pushRecent(recentIds: string[], id: string): string[] {
	return [id, ...recentIds.filter((existing) => existing !== id)].slice(0, RECENT_LIMIT);
}
